// client/src/components/Items.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { buildSplatPath } from '../utils/routeParams';

interface Item {
  name: string;
  enabled: boolean;
}

const Items: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [productionError, setProductionError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadItems = async (signal?: AbortSignal) => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/items`, signal ? { signal } : undefined);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      if (signal?.aborted) return;
      setItems(data);
      setProductionError(null);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error('Fetch items error:', err);
      setProductionError('Failed to fetch items: ' + err.message);
    }
  };

  useEffect(() => {
    const abort = new AbortController();
    void loadItems(abort.signal);
    return () => abort.abort();
  }, []);

  const handleAddItem = () => {
    navigate('/items/new');
  };

  const handleDeleteItems = async () => {
    if (selectedItems.length === 0) {
      setProductionError('No items selected to delete');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/items`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ names: selectedItems }),
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      setSelectedItems([]);
      await loadItems();
    } catch (err: any) {
      console.error('Delete items error:', err);
      setProductionError('Failed to delete items: ' + err.message);
    }
  };

  const handleToggleEnable = async (enable: boolean) => {
    if (selectedItems.length === 0) {
      setProductionError('No items selected to enable/disable');
      return;
    }
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/items`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ names: selectedItems, enabled: enable }),
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      setSelectedItems([]);
      await loadItems();
    } catch (err: any) {
      console.error('Toggle enable error:', err);
      setProductionError(`Failed to ${enable ? 'enable' : 'disable'} items: ` + err.message);
    }
  };

  const handleCheckboxChange = (name: string) => {
    setSelectedItems((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  return (
    <div className="page-container" style={{ overflowY: 'auto', maxHeight: '100vh' }}>
      <h2 className="detail-heading app-header" style={{ fontSize: '24px', marginBottom: '20px' }}>Items List</h2>
      {productionError && <div className="error">{productionError}</div>}
      <div className="inventory-actions">
        <button onClick={handleAddItem}>Add Item</button>
        <button onClick={handleDeleteItems} disabled={selectedItems.length === 0}>Delete Item</button>
        <button onClick={() => handleToggleEnable(true)} disabled={selectedItems.length === 0}>Enable Item</button>
        <button onClick={() => handleToggleEnable(false)} disabled={selectedItems.length === 0}>Disable Item</button>
      </div>
      <div className="inventory-table-container">
        <table className="inventory-table">
          <thead>
            <tr>
              <th>Select</th>
              <th>Item Name</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.name}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.name)}
                    onChange={() => handleCheckboxChange(item.name)}
                  />
                </td>
                <td>
                  <Link to={buildSplatPath('/items', item.name)}>{item.name}</Link>
                </td>
                <td>{item.enabled ? 'Enabled' : 'Disabled'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Items;